import { tenantTimezoneAtom } from "@/atoms/tenant-clock"
import { Card, Text } from "@/components/ui"
import type { PrizeDraw } from "@/domain/marketing/api"
import { prizeItemWords, prizeOfferLabel, type PrizeOffer } from "@/domain/marketing/prize-offer"
import { prizeValidity } from "@/domain/marketing/won-prize"
import { useReceivedCoupon } from "@/hooks/use-coupon-readiness"
import { useIssuedPrize } from "@/hooks/use-issued-prize"
import { usePrizeOffer, useRedeemPrize, type PrizeRedemption } from "@/hooks/use-redeem-prize"
import type { CouponBenefit } from "@/lib/coupon-benefit"
import { prizeNotUsableMessage } from "@/lib/coupon-readiness"
import { useAtomValue } from "jotai"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { PrizeGameButton } from "../PrizeGameButton"
import { PrizeArt } from "../PrizeArt"
import { PrizeChoiceList } from "./PrizeChoiceList"
import { PrizeCodeDisclosure } from "./PrizeCodeDisclosure"

type PrizeRedeemCardProps = {
  draw: PrizeDraw
  headline: boolean
  onLeave: () => void
}

const ORDER_BENEFIT: CouponBenefit = { kind: "order" }

function redemptionOf(offer: PrizeOffer): PrizeRedemption | null {
  if (offer.kind === "use") return { kind: "use" }
  if (offer.kind === "menu") return { kind: "menu" }
  if (offer.kind === "add") return { kind: "item", choice: offer.choice }
  return null
}

/**
 * What a win turns into on the game sheet: the prize (unless the ticket shows
 * it), its validity and ONE way to spend it — attach it to the order, add the
 * prize product to the bag, or pick it among the eligible ones. The code waits
 * behind "Ver código"; a prize that does not work now says when it will.
 */
export function PrizeRedeemCard({ draw, headline, onLeave }: PrizeRedeemCardProps) {
  const navigate = useNavigate()
  const timeZone = useAtomValue(tenantTimezoneAtom)
  const issued = useIssuedPrize(draw)
  const code = issued?.status === "ready" ? issued.code : null
  const received = useReceivedCoupon(code)
  const checked = received !== null && received.readiness.status !== "checking"
  const offer = usePrizeOffer(received?.details?.benefit ?? (checked ? ORDER_BENEFIT : null))
  const redeem = useRedeemPrize(onLeave)
  const [picking, setPicking] = useState(false)
  const label = draw.prizeLabel ?? "Um prêmio"
  const itemWords = prizeItemWords(received?.terms)
  const validity = issued?.status === "ready" ? prizeValidity(issued.prize?.expiresAt, timeZone) : null
  const readiness = received?.readiness
  const usable = readiness?.status === "usable" || readiness?.status === "unknown"
  const blocked = readiness && readiness.status !== "checking" && !usable ? prizeNotUsableMessage(readiness) : null
  const preparing = issued?.status === "issuing" || readiness?.status === "checking"
  const note = issued?.status === "late" ? "Seu prêmio ainda está sendo preparado. Ele fica guardado em Minha conta, em Meus prêmios." : blocked

  function act(current: PrizeOffer) {
    if (!code) return
    const redemption = redemptionOf(current)
    if (redemption) redeem(code, redemption)
    else setPicking(true)
  }

  return (
    <Card variant="game" radius="sheet" className="flex w-full flex-col items-stretch gap-3 text-center">
      {headline ? (
        <div className="flex flex-col items-center gap-1">
          <PrizeArt artKey={draw.prizeArtKey} label={label} size={56} />
          <Text size="game-eyebrow" weight="bold" uppercase tracking="ticket" tone="game-accent">
            Você ganhou
          </Text>
          <Text size="2xl" leading="tight" family="game-display">
            {itemWords?.headline ?? label}
          </Text>
          {itemWords?.detail ? (
            <Text size="sm" tone="muted">
              {itemWords.detail}
            </Text>
          ) : null}
        </div>
      ) : null}
      {validity ? (
        <Text size="sm" weight="semibold" tone="muted">
          {validity}
        </Text>
      ) : null}
      <Text role="status" aria-live="polite" size="sm" leading="relaxed" tone="muted" className={note ? undefined : "sr-only"}>
        {preparing ? "Preparando seu prêmio…" : note}
      </Text>
      {preparing ? <PrizeGameButton loading>Preparando seu prêmio…</PrizeGameButton> : null}
      {issued?.status === "late" ? (
        <PrizeGameButton
          onClick={() => {
            onLeave()
            navigate("/profile")
          }}
        >
          Ver meus prêmios
        </PrizeGameButton>
      ) : null}
      {offer && usable && code && !(picking && offer.kind === "pick") ? (
        <PrizeGameButton onClick={() => act(offer)}>{prizeOfferLabel(offer)}</PrizeGameButton>
      ) : null}
      {offer?.kind === "pick" && picking && code ? (
        <PrizeChoiceList tone="game" choices={offer.choices} onChoose={(choice) => redeem(code, { kind: "item", choice })} />
      ) : null}
      {code ? <PrizeCodeDisclosure code={code} tone="game" /> : null}
    </Card>
  )
}
